/**
 * MAPP compliance — shared by the server and the client bundles.
 *
 * See docs/promo-engine/mapp-pricing/mapp-pricing.md.
 *
 * IMPORTANT: this module must stay dependency-free. `resources/js` imports it by
 * RELATIVE PATH (the `App/*` mapping in tsconfig is type-only — webpack has no
 * matching alias, so a runtime `App/...` import would not bundle), so anything it
 * pulls in ends up in the browser bundle.
 *
 * The promo carries the level; the product's pricing carries `isMAPPConstrained`.
 * A level only bites on constrained products — `hidden` excepted, which suppresses
 * the whole promo regardless. `mappLevel` / `mappLabel` are the only functions that
 * read raw fields.
 *
 * WHO IS LOOKING (`MappViewer`): guests get every rule below. A logged-in customer who
 * is not MAPP-verified is treated like a guest for ONE thing only — promos on a
 * MAPP-sensitive brand are hidden from them outright (`promoHiddenFromViewer`); their
 * price is never hidden and they get no label. A verified customer sees everything.
 *
 * TWO INDEPENDENT HALVES, by data source — they share only `MappLabel`,
 * `MAPP_LABEL_COPY` and `MappViewer`:
 *
 * 1. SERVER, from raw promo objects (`MappFlags`): `resolveMappTreatment` does the
 *    precedence reduce, `promoHiddenFromViewer` / `promoHidesItsLabel` are the
 *    per-promo predicates (`promoHiddenFromGuests` is the guest branch, kept for the
 *    sitemap, which is anonymous by nature).
 * 2. CLIENT, from an Algolia hit (`MappHitPromotions`): the index ships flags already
 *    resolved across the product's promos AND against `isMAPPConstrained`, so there is
 *    nothing to compute — `resolveHitPricing` and `visiblePromoLabels` are direct reads.
 *    Do NOT re-apply the constraint here; that double-counts and over-hides.
 *
 * Don't try to unify them. The shapes differ because the index pre-resolves, and the
 * two halves are deliberately separate types.
 */

/**
 * MAPP compliance level, ordered least → most restrictive. Precedence across a
 * product's promos is a max over this ordering.
 *
 * The names are Howard's and they are genuinely confusing, so read this before
 * assuming what they mean:
 *
 * - `visible`      — default. Nothing is suppressed.
 *
 * - `hide-pricing` — hide the PRICE, but the customer may still see that the
 *                    product is part of a promo (the green promo label shows).
 *                    Used when a supplier only cares that the number isn't
 *                    advertised.
 *
 * - `cart-only`    — hide the price AND the fact that the product is in a promo
 *                    at all, until it's in the cart. Used when a supplier
 *                    doesn't want the deal itself advertised, not just the
 *                    number.
 *
 * - `hidden`       — the escape hatch. Hides the ENTIRE promo from guests
 *                    everywhere; logged-in users still see it. Reached for when
 *                    a supplier wants something the other levels can't express
 *                    (e.g. no pricing even in the cart). Note this turns the
 *                    PROMO off — the product itself still prices and sells
 *                    normally, at its non-promo price.
 *
 * WHY THE NAMES MISLEAD: both `hide-pricing` and `cart-only` hide the price, and
 * both reveal it once the item is in the cart — so "cart only" does not describe
 * anything unique about where the price appears. The ONLY difference between
 * them is whether the promo label is shown. `cart-only` is really
 * "hide-pricing + hide the fact there's a promo". Don't rename them: Howard
 * asked for these names specifically.
 *
 * MAPP never gates add-to-cart at any level — stock, `canBeOversold` and
 * license restrictions are the only things that disable the button.
 *
 * SCOPE: these levels live on the promo but apply PER PRODUCT — only to products
 * that actually carry a MAPP price. A promo can mix MAPP-bound and unrestricted
 * products, and the unrestricted ones price normally regardless of the level.
 * `hidden` is the exception: it applies to the whole promo. The per-product signal is
 * `Pricing.isMAPPConstrained` — see docs/promo-engine/mapp-pricing/.
 */
export type MappLevel = 'visible' | 'hide-pricing' | 'cart-only' | 'hidden';

/**
 * Which copy replaces a suppressed price. Defaults to `none`, and falls back to
 * `none` when two applicable promos disagree.
 *
 * - `none`  — render nothing. The price area is simply blank, with no
 *             explanation of why. This is deliberate, not an oversight.
 * - `cart`  — "Add to cart to see pricing"
 * - `login` — "Log in for pricing" (currently still shipping the older
 *             "Sign in for details" wording — see MAPP_LABEL_COPY)
 */
export type MappLabel = 'none' | 'cart' | 'login';

/** The minimum shape needed to derive a level. Satisfied by a `Promotion`. */
export type MappFlags = {
  mappComplianceLevel?: MappLevel | null;
  mappPricingLabel?: MappLabel | null;
  /**
   * Whether the promo's own brand is MAPP-sensitive. The API sends it on every promo object
   * (`false` for promos with no brand). Absent on a cache entry written before the field existed:
   * `promoHiddenFromViewer` reads that as `false`, while `carriesMappInputs` (PromoUtils) treats it
   * as "stale, refetch" for an unverified customer.
   */
  brandIsMappSensitive?: boolean | null;
};

/**
 * Who is looking. Three states, resolved once per request:
 *
 * - guest               — `isGuest: true`. Every level applies; the original MAPP rules.
 * - unverified customer — logged in, `isMappVerified: false`. Promos on a MAPP-sensitive brand are
 *                         hidden unless `visible`; NOTHING else changes — no price hiding, no label.
 *                         Once restricted promos are filtered out, the rest renders as for any
 *                         logged-in user.
 * - verified customer   — `isMappVerified: true`. Sees everything.
 *
 * A logged-in user with no linked customer yet (mid-registration) is NOT a guest and NOT verified.
 * Build it server-side with `mappViewerFor()` / `getMappViewer()` (UtilsForBackend); the client
 * reads it off `<body data-cid data-mapp-verified>` (layouts/main.edge).
 */
export type MappViewer = { isGuest: boolean; isMappVerified: boolean };

/**
 * The guest flag block on an Algolia hit's `promotions` aggregate.
 *
 * These apply to LOGGED-OUT users. The one logged-in reader is `visiblePromoLabels`, which falls
 * back to `hidePromoLabel` for an unverified customer when the record has no `unverified` block.
 *
 * FULLY RESOLVED as of 2026-08-14: the index applies both the promo's compliance level
 * AND the product's `price.isMAPPConstrained` when building these, so they can be read
 * directly. Don't re-apply the constraint at a call site — that would double-count and
 * over-hide (`hidden` is promo-wide and deliberately ignores the constraint).
 */
export type MappHitGuestFlags = {
  hidePricing?: boolean;
  /** Parallel to `qualifier.label` — one entry per promo, so we know WHICH badge to drop. */
  hidePromoLabel?: boolean[];
  hiddenPriceLabel?: MappLabel | null;
};

/**
 * The Algolia `hit.promotions` aggregate.
 *
 * The index ships PRE-RESOLVED flags rather than a level — it has already collapsed
 * every promo on the product, so there is no precedence reduce to mirror client-side.
 * That's why this shape differs from `MappFlags`; keep them separate rather than
 * trying to unify them.
 */
export type MappHitPromotions = {
  qualifier?: { label?: string[] };
  /**
   * Absent on any record the indexer hasn't reached. Those carry no flags anywhere, so they
   * resolve to no suppression — i.e. FAIL OPEN. That's why the production index must be reindexed
   * BEFORE this ships (see ProductionDeployPlaybook.md): a missing block is indistinguishable from
   * a product with nothing to hide, and it cannot be guarded in code.
   */
  guest?: MappHitGuestFlags;
  /**
   * Flags for a logged-in customer who is not MAPP-verified. Badge suppression only: an unverified
   * customer never has a price hidden, so there is deliberately no `hidePricing` /
   * `hiddenPriceLabel` here. Parallel to `qualifier.label`, like `guest.hidePromoLabel`.
   *
   * Absent on a record the indexer hasn't reached. Unlike a missing `guest` block this CAN be
   * guarded: `visiblePromoLabels` falls back to the guest array, which is a superset, so a stale
   * record over-hides a badge rather than leaking one.
   */
  unverified?: { hidePromoLabel?: boolean[] };
};

const MAPP_LEVEL_RANK: Record<MappLevel, number> = {
  'visible': 0,
  'hide-pricing': 1,
  'cart-only': 2,
  'hidden': 3,
};

/**
 * Copy for each label. The only place these strings live — never inline them at
 * a call site.
 *
 * NOTE: the spec calls for "Log in for pricing", but every price-hidden surface
 * ships "Sign in for details" today. Keeping the current wording so introducing
 * this map changes no rendered output; flip it when the copy decision is made.
 */
export const MAPP_LABEL_COPY: Record<MappLabel, string> = {
  'none': '',
  'cart': 'Add to cart to see pricing',
  'login': 'Sign in for details',
};

/**
 * Product-level treatment: the aggregate of every promo a product is in.
 *
 * SCOPE: these flags belong to the *product*. Whether a given promo is surfaced
 * at all (`hidden`) is a property of that promo — filter those out with
 * `promoHiddenFromGuests` first. Feeding an unfiltered list to the resolver
 * would let one promo's `hidden` level mask another promo's price suppression.
 *
 * All flags are suppressions with the same polarity, so all-false plus label
 * `none` is exactly the `visible` level.
 */
export type MappTreatment = {
  /**
   * Suppress the promo badge. PRODUCT-LEVEL, and only correct for the Algolia path, where
   * the index has already collapsed every promo into one flag. Server-side use
   * `promoHidesItsLabel()` instead — badge suppression is per promo.
   */
  hidePromoLabel: boolean;
  /** Suppress every rendered price for this product. */
  hidePricing: boolean;
  /** Copy that replaces a suppressed price; `none` renders nothing. */
  label: MappLabel;
};

// NOTE: there is deliberately no add-to-cart flag. MAPP never gates the button at any
// level — stock, canBeOversold and license restrictions are the only things that do.

/** Fully permissive — what logged-in users always get, and the `visible` level. */
export const MAPP_VISIBLE: MappTreatment = {
  hidePromoLabel: false,
  hidePricing: false,
  label: 'none',
};

/**
 * What the Algolia path resolves to: price suppression only.
 *
 * Badge suppression is deliberately NOT here. It's per promo, so a single product-level
 * boolean can't express it — use `visiblePromoLabels()` for badges.
 */
export type MappPriceTreatment = {
  hidePricing: boolean;
  label: MappLabel;
};

/** Fully permissive price treatment. */
export const MAPP_PRICE_VISIBLE: MappPriceTreatment = {
  hidePricing: false,
  label: 'none',
};

/** The promo's level. Absent or null means unrestricted. */
export function mappLevel(promo: MappFlags | undefined): MappLevel {
  return promo?.mappComplianceLevel ?? 'visible';
}

/** The promo's label. The API sends `null` when unset, which means "no label". */
export function mappLabel(promo: MappFlags | undefined): MappLabel {
  return promo?.mappPricingLabel ?? 'none';
}

/**
 * True when this promo must not be surfaced to guests at all.
 *
 * The guest branch of `promoHiddenFromViewer`. Only the sitemap should call this directly — it is
 * anonymous by nature. Everything else has a viewer and must use `promoHiddenFromViewer`.
 */
export function promoHiddenFromGuests(promo: MappFlags | undefined): boolean {
  return mappLevel(promo) === 'hidden';
}

/**
 * True when this promo must not be surfaced to the viewer at all — off /promos, 404 on its archive,
 * no badge, no callout, no cart group, no drawer.
 *
 * - Guest: `hidden` only. The original rule, unchanged.
 * - Unverified customer: any level other than `visible` on a MAPP-sensitive brand. The API collapses
 *   a non-`visible` level on a sensitive brand to `hidden` at import time, so in practice only
 *   `hidden` ever matches; the `!== 'visible'` comparison stays so an un-collapsed promo cannot leak.
 *   Promos with no brand, or a non-sensitive brand, are never affected.
 * - Verified customer: never.
 *
 * Reads `brandIsMappSensitive === true`, so a promo that predates the field (stale cache) is NOT
 * hidden from an unverified customer here. That is the one fail-open in this rule; the caches are
 * flushed on deploy, and `carriesMappInputs` forces a refetch on the product path.
 */
export function promoHiddenFromViewer(promo: MappFlags | undefined, viewer: MappViewer): boolean {
  if (viewer.isGuest) {
    return promoHiddenFromGuests(promo);
  }
  if (viewer.isMappVerified) {
    return false;
  }
  return promo?.brandIsMappSensitive === true && mappLevel(promo) !== 'visible';
}

/**
 * True when this promo must not advertise that the product belongs to it — no badge, no
 * callout, no entry in the promo chooser.
 *
 * PER PROMO, not per product. `hidePricing` is genuinely product-level (there is one price,
 * so any promo hiding it hides it), but suppressing a promo's badge says nothing about the
 * other promos on the same product: a `cart-only` promo hides its own badge and leaves an
 * unrelated `visible` promo's badge alone.
 *
 * Like every level except `hidden`, it only bites on a product carrying a MAPP price.
 * `hidden` promos are normally filtered out upstream by `promoHiddenFromGuests`; they are
 * included here so a caller using this predicate alone still does the right thing.
 *
 * NOTE the Algolia path can't use this — the index collapses to one product-level
 * `hidePromoLabel` alongside parallel per-promo label arrays, so it can't say WHICH label to
 * drop. See MappTreatment.hidePromoLabel and docs/promo-engine/mapp-pricing/STATUS.md.
 */
export function promoHidesItsLabel(promo: MappFlags | undefined, isMAPPConstrained = false): boolean {
  const level = mappLevel(promo);
  if (level === 'hidden') {
    return true;
  }
  return isMAPPConstrained && level === 'cart-only';
}

/** Behaviour for a single already-resolved level. */
export function treatmentForLevel(level: MappLevel, label: MappLabel): MappTreatment {
  switch (level) {
    case 'cart-only':
      return { hidePromoLabel: true, hidePricing: true, label };
    case 'hide-pricing':
      return { hidePromoLabel: false, hidePricing: true, label };
    case 'hidden':
      // Turns the PROMO off — the product still prices and sells normally.
      return { hidePromoLabel: true, hidePricing: false, label: 'none' };
    default:
      return MAPP_VISIBLE;
  }
}

/**
 * Reduce a product's guest-visible promos to one treatment.
 *
 * `isMAPPConstrained` comes from the product's pricing (`Pricing.isMAPPConstrained`).
 * A promo's level only applies to products that carry a MAPP price — an unconstrained
 * product in a `hide-pricing` promo prices normally. Defaults to `false`, so a caller
 * that can't supply it gets no suppression rather than over-hiding.
 *
 * Pass the list with viewer-hidden promos ALREADY REMOVED (see `promoHiddenFromViewer`) —
 * `hidden` is promo-level and is not subject to the per-product check, so callers
 * filter it separately.
 *
 * `isGuest` is `viewer.isGuest`, deliberately not the whole viewer: price suppression is
 * guest-only, so an unverified customer passes `false` here and gets `MAPP_VISIBLE`.
 *
 * Level: most restrictive wins (`visible < hide-pricing < cart-only`).
 *
 * Label: only promos that actually hide pricing contribute one — a `visible`
 * promo has no price to replace, so its implicit `none` must not drag a real
 * label down. If the contributing promos disagree, fall back to `none`.
 */
export function resolveMappTreatment(
  promos: MappFlags[],
  isGuest: boolean,
  isMAPPConstrained = false,
): MappTreatment {
  if (!isGuest || !promos.length || !isMAPPConstrained) {
    return MAPP_VISIBLE;
  }

  const applicable = promos.filter((p) => !promoHiddenFromGuests(p));
  if (!applicable.length) {
    return MAPP_VISIBLE;
  }

  const level = applicable
    .map(mappLevel)
    .reduce<MappLevel>((worst, next) => (MAPP_LEVEL_RANK[next] > MAPP_LEVEL_RANK[worst] ? next : worst), 'visible');

  const labels = applicable
    .filter((p) => {
      const l = mappLevel(p);
      return l === 'hide-pricing' || l === 'cart-only';
    })
    .map(mappLabel);
  const label = labels.length && labels.every((l) => l === labels[0]) ? labels[0] : 'none';

  return treatmentForLevel(level, label);
}

/**
 * A single Algolia hit's price treatment. The index ships these already resolved across
 * the product's promos AND against its `isMAPPConstrained`, so this is a direct read.
 *
 * Price suppression only — for badges use `visiblePromoLabels()`.
 */
export function resolveHitPricing(
  promotions: MappHitPromotions | undefined,
  isGuest: boolean,
): MappPriceTreatment {
  if (!isGuest || !promotions) {
    return MAPP_PRICE_VISIBLE;
  }

  const flags = promotions.guest;
  return {
    hidePricing: flags?.hidePricing === true,
    label: flags?.hiddenPriceLabel ?? 'none',
  };
}

/**
 * The promo labels the viewer may see for this hit, in index order.
 *
 * Badge suppression is PER PROMO — a `cart-only` promo drops its own badge and leaves an
 * unrelated `visible` promo's alone. The index resolves the scope difference for us:
 * `hidden` sets the flag regardless of the product, `cart-only` only on a constrained one.
 *
 * Guests read `guest.hidePromoLabel`. Unverified customers read `unverified.hidePromoLabel`,
 * falling back to the guest array on a record that predates it — the guest set is a superset
 * (both hide `hidden`; guests additionally hide constrained `cart-only`), so the fallback fails
 * closed. Verified customers read nothing.
 */
export function visiblePromoLabels(
  promotions: MappHitPromotions | undefined,
  viewer: MappViewer,
): string[] {
  const labels = promotions?.qualifier?.label ?? [];
  if (!labels.length || (!viewer.isGuest && viewer.isMappVerified)) {
    return labels;
  }

  const suppressed = viewer.isGuest
    ? promotions?.guest?.hidePromoLabel ?? []
    : promotions?.unverified?.hidePromoLabel ?? promotions?.guest?.hidePromoLabel ?? [];
  return labels.filter((_, i) => suppressed[i] !== true);
}
